/**
 * EVERY sample value the Brand page alone shows lives here; the ones it shares with the
 * Product page live in `explore-sample.ts`.
 *
 * The page's real half comes from `GET /brands/{brandId}/page` and
 * `GET /rules/applicable`. The concepts below have NO backing model in ATLAS yet, so
 * they are rendered from fixed sample data behind a `SampleDataNotice`, never mixed
 * silently into real content.
 *
 * Each export names the model that would replace it. Retiring one is meant to be
 * mechanical: delete the export, drop the card's notice, wire the real field.
 *
 * A real field that is merely EMPTY (a null `description`, an untagged brand) is NOT
 * sample data — the page renders its gap. Only unbacked concepts appear here.
 */

/** Would come from: a brand-level completeness score against an expected-elements template. */
export type SampleCompleteness = 'complete' | 'partial' | 'missing'

export interface SampleOwner {
	name: string
	role: string
}

/**
 * The tagline shown when a brand records none of its own.
 *
 * NOT the brand's words — a placeholder behind a `SampleDataNotice`, kept so the hero never
 * loses the row entirely. A brand that HAS a tagline reads it from `brand.meta.tagline`
 * (`brand-meta.ts`), written by the brand-profile seed.
 */
export const SAMPLE_TAGLINE = 'Long-lasting protection, in a single move.'

/**
 * The hero's placeholder when no logo resolves.
 *
 * A brand's logo is now an ASSET — the first file classified `file_type=logo` to it — so this
 * shows only where none is classified, or while its thumbnail is still being generated.
 */
export const SAMPLE_HERO_LABEL = 'BRAND HERO'

/** Would come from: an axis-value contacts model (brand owner / product manager). */
export const SAMPLE_OWNER: SampleOwner = { name: 'Elena Gomez', role: 'Global Brand Manager' }

/**
 * Would come from: a per-product completeness score. Assigned by position so the grid
 * shows the design's mix of states — deliberately arbitrary, because any value derived
 * from real columns would look like a score and mean nothing.
 */
const COMPLETENESS_CYCLE: SampleCompleteness[] = ['complete', 'partial', 'complete', 'missing']

export const sampleCompletenessFor = (index: number): SampleCompleteness =>
	COMPLETENESS_CYCLE[index % COMPLETENESS_CYCLE.length] as SampleCompleteness

/** Would come from: a product image (`product.image_ref` exists but is null for every row). */
export const SAMPLE_PRODUCT_THUMB_LABEL = 'PKG'
